import pkceChallenge from "pkce-challenge";
import ClientOAuth2 from "client-oauth2";
import jwt_decode from "jwt-decode";
class AuthenticationClass {
  _tokenUrl = process.env.VUE_APP_OAUTH2_TOKEN_URL;
  _authorizeUrl = process.env.VUE_APP_OAUTH2_AUTHORIZE_URL;
  _redirectUrl = process.env.VUE_APP_OAUTH2_REDIRECT_URL;
  _clientId = process.env.VUE_APP_OAUTH2_CLIENT_ID;
  _logoutFns = [];

  /**
   * The local Token
   * @type {null|ClientOAuth2.Token}
   * @private
   */
  _token = null;
  _me = null;

  isLoggedIn() {
    if (this._token !== null) {
      return !this._token.expired();
    }
    return false;
  }

  /**
   * Call these functions when a logout request is made or a token become invalid.
   * @param fn
   */
  onLogout(fn) {
    this._logoutFns.push(fn);
  }

  /**
   * Refresh the token
   * @returns {Promise<unknown>}
   */
  refreshAccessToken() {
    return this._refreshToken();
  }

  /**
   * Get the current Access token or null.
   * @returns {string|null}
   */
  getAccessToken() {
    let token = this._getToken();
    if (token == null) {
      return null;
    }
    return token.accessToken;
  }

  /**
   * Initialize the token state
   */
  init() {
    if (this._hasLocalToken()) {
      this._getToken();
    }
  }

  /**
   * Attempt to login the current user.
   * @returns {Promise<null>}
   */
  login() {
    return new Promise((resolve, reject) => {
      /*
       * If the user is already logged in
       */
      if (this.isLoggedIn()) {
        resolve();
        return;
      }

      /*
       * If there is a local token, try to use that
       */
      if (this._hasLocalToken()) {
        /*
         * If the local token has expired refresh it.
         */
        if (this._token.expired()) {
          this._refreshToken()
            .then(() => {
              resolve();
            })
            .catch(() => {
              this._clearToken();

              /*
               * if the refresh failed then start the flow again
               */
              this._oauth2RedirectFlow()
                .then(() => {
                  resolve();
                })
                .catch(() => {
                  reject("REFRESH_FAILED");
                });
            });
        }
        resolve();
      }
      if (this._token === null) {
        this._oauth2RedirectFlow()
          .then(() => {
            resolve();
          })
          .catch(err => {
            reject(err);
          });
      }
    });
  }

  /**
   * Logout a user.
   *
   * @returns {Promise<unknown>}
   */
  logout() {
    return new Promise(resolve => {
      this._clearToken();
      this._processLogoutEvents("LOGOUT");
      resolve();
    });
  }

  /**
   * Get a value from the token.
   * @param attribute
   * @returns {null|*}
   * @private
   */
  _getTokenValue(attribute) {
    let token = this._getToken();
    if (token == null) {
      return null;
    }
    let data = jwt_decode(token.accessToken);
    if (data.hasOwnProperty(attribute)) {
      return data[attribute];
    }
    return null;
  }

  /**
   * Run the logout events.
   * @param type {string} The type of logout event
   * @private
   */
  _processLogoutEvents(type) {
    for (let i in this._logoutFns) {
      if (
        this._logoutFns.hasOwnProperty(i) &&
        typeof this._logoutFns[i] == "function"
      ) {
        this._logoutFns[i](type);
      }
    }
  }

  /**
   * Get the Token from Local Storage
   * @returns {null|ClientOAuth2.Token}
   */
  _getToken() {
    if (this._token !== null) {
      return this._token;
    }
    let token = window.localStorage.getItem("t");
    if (token !== null) {
      let data = JSON.parse(atob(token));
      data.expires = new Date(data.expiresAt);
      token = this._getOauth2Client().createToken(
        data.access_token,
        data.refresh_token,
        data.token_type,
        data
      );
      token.expires = new Date(data.expiresAt);
      this._token = token;
      return this._token;
    }
    return null;
  }

  /**
   * Store the token locally.
   * @param token {ClientOAuth2.Token}
   */
  _setToken(token) {
    let data = token.data;
    data.expiresAt = token.expires.getTime();
    window.localStorage.setItem("t", btoa(JSON.stringify(data)));
    this._token = token;
  }

  /**
   * Clear the loggedIn token
   * @private
   */
  _clearToken() {
    this._token = null;
    window.localStorage.removeItem("t");
  }

  /**
   * Check to see if there's a local token stored.
   * @returns {boolean}
   */
  _hasLocalToken() {
    let token = window.localStorage.getItem("t");
    return token !== null;
  }

  /**
   * Refresh the token if it's expired.
   * @returns {Promise<unknown>}
   * @private
   */
  _refreshToken() {
    return new Promise((resolve, reject) => {
      this._token
        .refresh({
          clientId: this._clientId,
          accessTokenUri: this._tokenUrl,
          authorizationUri: this._authorizeUrl,
          redirectUri: this._redirectUrl
        })
        .then(token => {
          this._setToken(token);
          resolve();
        })
        .catch(() => {
          this._processLogoutEvents("REFRESH");
          reject();
        });
    });
  }

  _oauth2RedirectFlow() {
    return new Promise((resolve, reject) => {
      let flowData = this._getFlowData();
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      if (flowData === null || code === null) {
        flowData = this._generateFlowData(true);

        /*
         * Create an Oauth2 client
         */
        let Client = this._getOauth2Client(flowData.state);

        /*
         * Generate a redirect URL to the oauth2 server
         */
        window.location = Client.code.getUri({
          query: {
            code_challenge: flowData.challenge,
            code_challenge_method: "S256"
          }
        });
      } else {
        /*
         * Create an Oauth2 client
         */
        let Client = this._getOauth2Client(flowData.state);

        /*
         * With the Redirect URL attempt to get the users token.
         */
        Client.code
          .getToken(window.location.href, {
            body: {
              code_verifier: flowData.verify
            }
          })

          /**
           * @param {Token} token the Token returned from the server
           */
          .then(token => {
            this._setToken(token);
            resolve();
          })

          /**
           * If no token was able to be resolved
           */
          .catch(err => {
            reject("Unable to resolve token");
          });
      }
    });
  }

  /**
   * Login a user with the OAUTH2 User flow.
   *
   * @returns {Promise<unknown>}
   * @private
   */
  _oauth2PopupFlow() {
    return new Promise((resolve, reject) => {
      let flowData = this._generateFlowData();

      /*
       * Create an Oauth2 client
       */
      let Client = this._getOauth2Client(flowData.state);

      /*
       * Generate a redirect URL to the oauth2 server
       */
      let url = Client.code.getUri({
        query: {
          code_challenge: flowData.challenge,
          code_challenge_method: "S256"
        }
      });

      /*
       * Display a popup login form which will direct the user to
       * the OAUTH2 server
       */
      this._popupLogin(url)
        .then(redirectedUrl => {
          /*
           * With the Redirect URL attempt to get the users token.
           */
          Client.code
            .getToken(redirectedUrl, {
              body: {
                code_verifier: flowData.verify
              }
            })

            /**
             * @param {Token} token the Token returned from the server
             */
            .then(token => {
              this._setToken(token);
              resolve();
            })

            /**
             * If no token was able to be resolved
             */
            .catch(err => {
              reject("Unable to resolve token");
            });
        })

        /**
         * If the popup failed to load
         */
        .catch(() => {
          reject("Unable to display login form. Please allow the popup");
        });
    });
  }

  /**
   * Generate a popup frame that takes the user to the login server.
   * returns the full redirect URL with code and state
   * if the response is successful.
   *
   * @param {string} url The URL of the popup frame
   * @returns {Promise<string>}
   * @private
   */
  _popupLogin(url) {
    return new Promise((resolve, reject) => {
      let valid = false;
      let newWindow = window.open(url, "oauth2-auth", "height=600,width=800");
      if (newWindow === null) {
        reject("Unable to create frame");
      }
      if (window.focus) {
        newWindow.focus();
      }
      window.oauthPopupComplete = function() {
        valid = true;
        resolve(newWindow.location.href);
      };
      let timer = setInterval(function() {
        if (newWindow.closed) {
          clearInterval(timer);
          if (!valid) {
            reject("popup closed");
          }
        }
      }, 1000);
    });
  }

  /**
   * Setup the Oauth2 Flow
   * @returns {{verify: string, challenge: string, state: string}}
   * @private
   */
  _generateFlowData(store = false) {
    /*
     * Generate a PKCE challenge for  Oauth2 client authentication
     */
    let challenge = new pkceChallenge();

    /*
     * Generate a random string for CSRF
     */
    let state =
      Math.random()
        .toString(36)
        .substring(2, 15) +
      Math.random()
        .toString(36)
        .substring(2, 15);

    let flowData = {
      verify: challenge.code_verifier,
      challenge: challenge.code_challenge,
      state: state
    };
    console.log(store);
    if (store) {
      let enc = btoa(JSON.stringify(flowData));
      window.localStorage.setItem("at", enc);
    }
    return flowData;
  }

  _getFlowData() {
    let data = window.localStorage.getItem("at");
    window.localStorage.removeItem("at");
    if (data !== null) {
      return JSON.parse(atob(data));
    }
    return null;
  }

  /**
   *
   * @returns {ClientOAuth2}
   * @private
   */
  _getOauth2Client(state) {
    return new ClientOAuth2({
      clientId: this._clientId,
      accessTokenUri: this._tokenUrl,
      authorizationUri: this._authorizeUrl,
      redirectUri: this._redirectUrl,
      state: state
    });
  }
}

export const Authentication = new AuthenticationClass();
